import React, { useCallback, useMemo, useState } from "react";
import { ModalV2, Text, QueryLoader } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useGetContactsByPositionQuery } from "~/graphql/supergraphHooks";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { ProgramName } from "../../utils/constants";
import { useStartRemediation } from "../hooks";
import InviteInvestorFormContent from "./InviteInvestorFormContent";
import { schema } from "./schema";
import { modalSubtitle, modalHeader } from "./styles_vanilla.css";
import { FormValues } from "./types";

export type ContactEligibility = {
  email: string;
  isEligible: boolean;
  disabledReason?: string;
};

export type InviteInvestorModalProps = {
  positionId?: number;
  arenaId?: string;
  entityId?: string;
  programName: ProgramName;
  accountName?: string;
  entityName?: string;
  dateSent?: string;
  sentCount?: number;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
};

const InviteInvestorModal = ({
  positionId,
  arenaId,
  entityId,
  programName,
  accountName,
  entityName,
  dateSent,
  sentCount,
  onClose,
}: InviteInvestorModalProps) => {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contactsResult = useGetContactsByPositionQuery({
    variables: {
      arenaIds: [arenaId || ""],
      positionIds: [toGlobalId("PositionObjectType", positionId || 0)],
    },

    fetchPolicy: "no-cache",
    skip: !arenaId || !positionId,
  });

  const { startRemediation, loading: sendingInvitation } = useStartRemediation({
    successMessage: "Invitation sent successfully",
  });

  const selectedPosition = contactsResult.data?.positions?.edges[0]?.node;

  const defaultMessage = `To ensure that your account meets compliance requirements of the Fund(s), please submit the requested information and/or documentation for ${accountName}.`;

  const contactsEligibility = useMemo(() => {
    const edges = selectedPosition?.contacts?.edges;
    if (!edges) return [];

    const contacts: ContactEligibility[] = [];
    const seenEmails = new Set<string>();

    for (const contactEdge of edges) {
      const node = contactEdge?.node;
      const email = node?.primaryEmail?.emailAddress;

      if (!email || seenEmails.has(email)) continue;
      seenEmails.add(email);

      const portal = node?.portal;
      const portalActivityStatus =
        portal && "portalActivityStatus" in portal
          ? (portal as { portalActivityStatus?: string }).portalActivityStatus
          : undefined;
      const isEligible = portalActivityStatus === "Active";

      contacts.push({
        email,
        isEligible,
        disabledReason: !isEligible
          ? "Unable to send remediation request to this contact. The portal or entity has not been enabled for access."
          : undefined,
      });
    }

    return contacts;
  }, [selectedPosition?.contacts?.edges]);

  const formInitialValues: FormValues = useMemo(
    () => ({
      selectedEmails: contactsEligibility
        .filter((contact) => contact.isEligible)
        .map((contact) => contact.email),
      message: defaultMessage,
      showCustomMessage: false,
    }),
    [contactsEligibility, defaultMessage],
  );

  const handleHideModal = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setSubmitError(null);

        if (!arenaId || !entityId) {
          throw new Error("Arena ID and Entity ID are required");
        }

        const arenaDbId = toDbId(arenaId);
        const entityDbId = toDbId(entityId);

        await startRemediation({
          positionId: positionId!,
          arenaId: arenaDbId,
          emailAddresses: values.selectedEmails,
          message: values.showCustomMessage ? values.message : defaultMessage,
          entityId: entityDbId,
          programName: programName,
        });

        onClose(false);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Unable to send invitation. Please try again or contact support.";
        setSubmitError(errorMessage);
      }
    },
    [startRemediation, onClose, positionId, arenaId, entityId, programName, defaultMessage],
  );

  return (
    <QueryLoader result={contactsResult}>
      <ModalV2
        show={true}
        onHide={handleHideModal}
        isCentered={true}
        size="medium"
        backdrop={true}
        title="Remediation request"
        headerClassName={modalHeader}
      >
        <Form<FormValues>
          key={positionId}
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          yupValidationSchema={schema}
          submitOnlyChangedValues={false}
        >
          <ModalV2.Body>
            {accountName && entityName && (
              <Text variant="regular-md" className={modalSubtitle}>
                {accountName} in {entityName}
              </Text>
            )}
            <InviteInvestorFormContent
              contactsEligibility={contactsEligibility}
              onClose={onClose}
              loading={sendingInvitation}
              contactsError={contactsResult.error}
              submitError={submitError}
              dateSent={dateSent}
              sentCount={sentCount}
            />
          </ModalV2.Body>
        </Form>
      </ModalV2>
    </QueryLoader>
  );
};

export default InviteInvestorModal;
